import { MouseEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';

import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import {
  ROUTE_REGISTRY_TOSER_BY_ID,
  ROUTE_REGISTRY_SPV_BY_ID,
  ROUTE_REGISTRY_EGRUL_BY_ID,
  ROUTE_REGISTRY_MSP_BY_ID,
  ROUTE_REGISTRY_OEZ_BY_ID,
  ROUTE_REGISTRY_EGRIP_BY_ID,
} from 'CorePackage/MainModule/routes/routes';
import {
  getKnmDataId,
  getKnmDataIsIndividualEntrepreneur,
  getKnmDataIsLegalEntity,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { useRegistryCheck } from 'KnmPackage/MainModule/hooks/useRegistryCheck';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './ErknmRegistryCheck.module.scss';

enum CheckSubjectStatesEnum {
  NOT_RUN = 'NOT_RUN',
  RUN = 'RUN',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

function getCheckSubjectState(checkSubjectResult?: IKnmCheckSubjectResult) {
  if (checkSubjectResult && checkSubjectResult.requestTime) {
    if (typeof checkSubjectResult.egrip?.id === 'number' || typeof checkSubjectResult?.egrul?.id === 'number') {
      return CheckSubjectStatesEnum.SUCCESS;
    }

    return CheckSubjectStatesEnum.RUN;
    // return CheckSubjectStatesEnum.ERROR; // ToDo
  }

  return CheckSubjectStatesEnum.NOT_RUN;
}

interface IErknmRegistryCheckProps {
  className?: string;
  initialCheckSubjectResult: IKnmCheckSubjectResult | null;
  setSubjectResult?: (result: IKnmCheckSubjectResult | null) => void;
  isActOrResultDecision?: boolean;
}

interface IErknmRegistryCheckStoreProps {
  knmId: number | undefined;
  isIndividualEntrepreneur: boolean | null | undefined;
  isLegalEntity: boolean | null | undefined;
}

const ErknmRegistryCheckWithoutStore = ({
  className = '',
  initialCheckSubjectResult,
  setSubjectResult,
  isIndividualEntrepreneur,
  isLegalEntity,
  knmId,
  isActOrResultDecision,
}: IErknmRegistryCheckProps & IErknmRegistryCheckStoreProps) => {
  const { checkSubjectResult } = useRegistryCheck(knmId, initialCheckSubjectResult || undefined);
  const checkSubjectState: CheckSubjectStatesEnum = getCheckSubjectState(checkSubjectResult);

  useEffect(() => {
    setSubjectResult && setSubjectResult(checkSubjectResult || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkSubjectResult]);

  const handleRegistryClick = (id?: number | null) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (typeof id !== 'number') {
      event.preventDefault();
    }
  };

  const linkEgrulAttributes = {
    ...(typeof checkSubjectResult?.egrul?.id !== 'number' && { 'data-disabled': true }),
    ...(typeof checkSubjectResult?.egrul?.id === 'number' && { 'data-enabled': true }),
  };

  const linkEgripAttributes = {
    ...(typeof checkSubjectResult?.egrip?.id !== 'number' && { 'data-disabled': true }),
    ...(typeof checkSubjectResult?.egrip?.id === 'number' && { 'data-enabled': true }),
  };

  const linkMspAttributes = {
    ...(typeof checkSubjectResult?.msp?.id !== 'number' && { 'data-disabled': true }),
    ...(typeof checkSubjectResult?.msp?.id === 'number' && { 'data-enabled': true }),
  };

  const linkToserAttributes = {
    ...(typeof checkSubjectResult?.toser?.id !== 'number' && { 'data-disabled': true }),
    ...(typeof checkSubjectResult?.toser?.id === 'number' && { 'data-enabled': true }),
  };

  const linkSpvAttributes = {
    ...(typeof checkSubjectResult?.spv?.id !== 'number' && { 'data-disabled': true }),
    ...(typeof checkSubjectResult?.spv?.id === 'number' && { 'data-enabled': true }),
  };

  const linkOezAttributes = {
    ...(typeof checkSubjectResult?.oez?.id !== 'number' && { 'data-disabled': true }),
    ...(typeof checkSubjectResult?.spv?.id === 'number' && { 'data-enabled': true }),
  };

  return (
    <div className={className ? `${styles.Wrapper} ${className}` : styles.Wrapper}>
      <ul className={styles.StatementList}>
        {(checkSubjectState === CheckSubjectStatesEnum.NOT_RUN ||
          checkSubjectState === CheckSubjectStatesEnum.RUN ||
          isLegalEntity ||
          typeof checkSubjectResult?.egrul?.id === 'number') && (
          <li className={styles.StatementItem}>
            <Link
              className={styles.Link}
              to={
                typeof checkSubjectResult?.egrul?.id === 'number'
                  ? ROUTE_REGISTRY_EGRUL_BY_ID(checkSubjectResult.egrul.id)
                  : '#'
              }
              target="_blank"
              onClick={handleRegistryClick(checkSubjectResult?.egrul?.id)}
              {...linkEgrulAttributes}
            >
              Выписка из ЕГРЮЛ
            </Link>
          </li>
        )}
        {(isIndividualEntrepreneur || typeof checkSubjectResult?.egrip?.id === 'number') && (
          <li className={styles.StatementItem}>
            <Link
              className={styles.Link}
              to={
                typeof checkSubjectResult?.egrip?.id === 'number'
                  ? ROUTE_REGISTRY_EGRIP_BY_ID(checkSubjectResult.egrip.id)
                  : '#'
              }
              target="_blank"
              onClick={handleRegistryClick(checkSubjectResult?.egrip?.id)}
              {...linkEgripAttributes}
            >
              Выписка из ЕГРИП
            </Link>
          </li>
        )}
        <li className={styles.StatementItem}>
          <Link
            className={styles.Link}
            to={
              typeof checkSubjectResult?.msp?.id === 'number'
                ? ROUTE_REGISTRY_MSP_BY_ID(checkSubjectResult.msp.id)
                : '#'
            }
            target="_blank"
            onClick={handleRegistryClick(checkSubjectResult?.msp?.id)}
            {...linkMspAttributes}
          >
            Выписка из МСП
          </Link>
        </li>
        {!isActOrResultDecision && (
          <>
            <li className={styles.StatementItem}>
              <Link
                className={styles.Link}
                to={
                  typeof checkSubjectResult?.toser?.id === 'number'
                    ? ROUTE_REGISTRY_TOSER_BY_ID(checkSubjectResult.toser.id)
                    : '#'
                }
                target="_blank"
                onClick={handleRegistryClick(checkSubjectResult?.toser?.id)}
                {...linkToserAttributes}
              >
                Выписка из реестра ТОСЭР
              </Link>
            </li>
            <li className={styles.StatementItem}>
              <Link
                className={styles.Link}
                to={
                  typeof checkSubjectResult?.spv?.id === 'number'
                    ? ROUTE_REGISTRY_SPV_BY_ID(checkSubjectResult.spv.id)
                    : '#'
                }
                target="_blank"
                onClick={handleRegistryClick(checkSubjectResult?.spv?.id)}
                {...linkSpvAttributes}
              >
                Выписка из реестра СПВ
              </Link>
            </li>
            <li className={styles.StatementItem}>
              <Link
                className={styles.Link}
                to={
                  typeof checkSubjectResult?.oez?.id === 'number'
                    ? ROUTE_REGISTRY_OEZ_BY_ID(checkSubjectResult.oez.id)
                    : '#'
                }
                target="_blank"
                onClick={handleRegistryClick(checkSubjectResult?.oez?.id)}
                {...linkOezAttributes}
              >
                Выписка из реестра ОЭЗ
              </Link>
            </li>
          </>
        )}
      </ul>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IErknmRegistryCheckStoreProps => ({
  isIndividualEntrepreneur: getKnmDataIsIndividualEntrepreneur(state),
  isLegalEntity: getKnmDataIsLegalEntity(state),
  knmId: getKnmDataId(state),
});

export const ErknmRegistryCheck = connect(mapStateToProps)(ErknmRegistryCheckWithoutStore);
